import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { API_CONFIG } from '@/lib/api/config';

export default function ShopifyCallback() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('Processing authorization...');

  useEffect(() => {
    const handleShopifyCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        const shop = urlParams.get('shop');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        if (error) {
          const errorMsg = errorDescription || error;

          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              success: false,
              error: errorMsg,
              source: 'shopify-oauth'
            }, window.location.origin);
          }

          setStatus('error');
          setMessage(`Authorization failed: ${errorMsg}`);

          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }

        if (!code || !state || !shop) {
          const errorMsg = 'Missing required OAuth parameters';

          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              success: false,
              error: errorMsg,
              source: 'shopify-oauth'
            }, window.location.origin);
          }

          setStatus('error');
          setMessage(errorMsg);

          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }

        setMessage('Exchanging authorization code for access token...');

        const callbackUrl = `${API_CONFIG.BASE_URL}/shopify/oauth/callback`;

        const exchangeRes = await fetch(callbackUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            state,
            shop,
          }),
        });


        let exchangeData;
        const contentType = exchangeRes.headers.get('content-type');
        const isJson = contentType && contentType.includes('application/json');

        try {
          if (isJson) {
            exchangeData = await exchangeRes.json();
          } else {
            const text = await exchangeRes.text();
            try {
              exchangeData = JSON.parse(text);
            } catch {
              throw new Error(text || `Server returned ${exchangeRes.status}: ${exchangeRes.statusText}`);
            }
          }
        } catch (parseError: any) {
          const errorMsg = parseError.message || 'Failed to parse server response. The server may be experiencing issues.';

          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              success: false,
              error: errorMsg,
              source: 'shopify-oauth'
            }, window.location.origin);
          }

          setStatus('error');
          setMessage(`Failed: ${errorMsg}`);

          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }


        if (!exchangeRes.ok || !exchangeData.ok) {
          const errorMsg = exchangeData.error || exchangeData.message || `Server returned ${exchangeRes.status}: ${exchangeRes.statusText}`;

          if (window.opener && !window.opener.closed) {
            window.opener.postMessage({
              success: false,
              error: errorMsg,
              source: 'shopify-oauth'
            }, window.location.origin);
          }

          setStatus('error');
          setMessage(`Failed: ${errorMsg}`);

          setTimeout(() => {
            window.close();
          }, 3000);
          return;
        }

        setStatus('success');
        setMessage('Authorization successful! You can close this window.');

        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            success: true,
            source: 'shopify-oauth',
            shop: exchangeData.shop
          }, window.location.origin);
        }

        setTimeout(() => {
          window.close();
        }, 2000);
      } catch (error: any) {
        const errorMsg = error.message || 'An unexpected error occurred';

        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            success: false,
            error: errorMsg,
            source: 'shopify-oauth'
          }, window.location.origin);
        }

        setStatus('error');
        setMessage(`Error: ${errorMsg}`);

        setTimeout(() => {
          window.close();
        }, 3000);
      }
    };

    handleShopifyCallback();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Shopify Authorization</CardTitle>
          <CardDescription>Processing your authorization request...</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-8 space-y-4">
          {status === 'loading' && (
            <>
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground text-center">{message}</p>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="h-8 w-8 text-green-600" />
              <p className="text-sm text-muted-foreground text-center">{message}</p>
            </>
          )}
          {status === 'error' && (
            <>
              <XCircle className="h-8 w-8 text-destructive" />
              <p className="text-sm text-destructive text-center">{message}</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

